import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { LineChart } from '@mui/x-charts/LineChart';
import type { GetDailyEventsDigestedResponse } from '@weft/api-definition';
import type { ItemOf } from '@weft/utils';
import { dayjs, extract, getMapValues, groupBy, sumBy } from '@weft/utils';
import { formatNumberAbbreviated } from 'apps/app/src/utils/number';
import { useMemo } from 'react';
import { EventsDigestedLineChartToolTip, getFormattedTooltipLabel } from './events-digested-line-chart-tooltip';
import { getMonthlyEventsGroupKey } from './events-digested-line-chart-utils';

export const DISPLAY_INTERVALS = Object.freeze({
  daily: 'daily',
  monthly: 'monthly',
});

export type EventsDigestedLineChartDisplayInterval = typeof DISPLAY_INTERVALS[keyof typeof DISPLAY_INTERVALS];

type EventsDigestedLineChartProps = {
  dailyEventsDigested: GetDailyEventsDigestedResponse['dailyEventsDigested'];
  displayInterval: EventsDigestedLineChartDisplayInterval;
  eventsPerMonth: number;
};

export const EventsDigestedLineChart: React.FC<EventsDigestedLineChartProps> = ({ dailyEventsDigested, displayInterval, eventsPerMonth }) => {
  const monthlyEvents = useMemo(() => {
    const eventsByMonth = groupBy(dailyEventsDigested, ({ date }) => getMonthlyEventsGroupKey(dayjs(date), eventsPerMonth));

    return getSummedMonthlyEventsMap(eventsByMonth);
  }, [dailyEventsDigested, eventsPerMonth]);

  const eventsToDisplay = displayInterval === DISPLAY_INTERVALS.daily ? dailyEventsDigested : monthlyEvents;

  const displayTickInterval = displayInterval === DISPLAY_INTERVALS.monthly ? eventsPerMonth : 2;

  return (
    <LineChart
      skipAnimation
      height={300}
      margin={{ right: 0 }}
      grid={{ horizontal: true }}
      slotProps={{ legend: { hidden: true } }}
      tooltip={{
        trigger: 'axis', axisContent: props => (
          <EventsDigestedLineChartToolTip
            displayInterval={displayInterval}
            eventsPerMonth={eventsPerMonth}
            {...props}
          />
        ), 
      }}
      sx={{
        [`.${axisClasses.root}`]: {
          [`.${axisClasses.tick}, .${axisClasses.line}`]: {
            stroke: 'none',
          },
        },
      }}
      xAxis={[{
        data: extract(eventsToDisplay, 'date'),
        valueFormatter: (value: Date, context) => context.location === 'tick' 
          ? getFormattedAxisLabel(value, displayInterval) 
          : getFormattedTooltipLabel(value, displayInterval, eventsPerMonth),
        scaleType: 'band',
        tickLabelInterval: (_, index) => index % displayTickInterval === 0,
        tickLabelStyle: {
          fill: '#0D1B27DE',
          fontFamily: 'OakesGrotesk',
          fontWeight: 400,
          fontSize: '12',
        },
      }]}
      yAxis={[{
        valueFormatter: (value: number) => formatNumberAbbreviated(value),
        tickLabelStyle: {
          fill: '#1B3D5599',
          fontFamily: 'OakesGrotesk',
          fontWeight: 400,
          fontSize: '12',
        },
      }]}
      series={[
        {
          data: extract(eventsToDisplay, 'count'),
          curve: 'linear',
          label: 'Events',
          showMark: false,
          color: '#C250FF',
        },
      ]}
    />
  );
};

function getSummedMonthlyEventsMap(eventsMap: Map<string, GetDailyEventsDigestedResponse['dailyEventsDigested']>) {
  return getMapValues(eventsMap).map<ItemOf<GetDailyEventsDigestedResponse['dailyEventsDigested']>>((events) => ({
    date: dayjs(events[0].date).toDate(),
    count: sumBy(events, 'count'),
  }));
} 

function getFormattedAxisLabel(date: Date, displayInterval: EventsDigestedLineChartDisplayInterval) {
  return displayInterval === DISPLAY_INTERVALS.monthly ? dayjs(date).format('MMM YY') : dayjs(date).format('MMM DD');
}